import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, Subject, catchError, finalize, throwError } from 'rxjs';
import { Bind } from 'lodash-decorators-esm';
import { ToastPosition, ToastService, ToastVariant } from '@adnoc/onehub-form-ui';
import { MicroFrontendMessageService, MicroFrontendPropsService } from '@adnoc/servicebox';

import { ISandboxMicrofrontendProps } from '../models/i-sandbox-microfrontend-props';

export const GENERIC_ERROR_MESSAGE = 'Something went wrong, please try again';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  static loadingStatus$: Subject<boolean> = new Subject<boolean>();
  private isInSandbox = false;

  constructor(
    private toastService: ToastService,
    private microFrontendPropsService: MicroFrontendPropsService,
    private microFrontendMessageService: MicroFrontendMessageService,
  ) {
    this.isInSandbox = (this.microFrontendPropsService.snapshot as ISandboxMicrofrontendProps).isInSandbox;
    this.toastService.setPosition(ToastPosition.Right);
  }

  warning(message: string, header?: string): void {
    if (this.isInSandbox) {
      this.toastService.warning(message, header);
    } else {
      this.microFrontendMessageService.showToastMessage('', 'show', 'warning', message);
    }
  }

  success(message: string, header?: string): void {
    if (this.isInSandbox) {
      this.toastService.success(message, header);
    } else {
      this.microFrontendMessageService.showToastMessage('', 'show', 'success', message);
    }
  }

  error(message: string, header?: string): void {
    if (this.isInSandbox) {
      this.toastService.error(message, header);
    } else {
      this.microFrontendMessageService.showToastMessage('', 'show', 'error', message);
    }
  }

  info(message: string, header?: string): void {
    if (this.isInSandbox) {
      this.toastService.info(message, ToastVariant.info, header);
    } else {
      this.microFrontendMessageService.showToastMessage('', 'show', 'info', message);
    }
  }

  @Bind()
  notificationErrorOperator<T>(errorMessage?: string, ignoreErrorCodes: number[] = []): MonoTypeOperatorFunction<T> {
    return (source: Observable<T>) =>
      source.pipe(
        catchError((data: HttpErrorResponse) => {
          if (!ignoreErrorCodes.includes(data.status)) {
            const message: string = errorMessage || data.error?.translationKey || GENERIC_ERROR_MESSAGE;
            this.error(message);
          }
          return throwError(() => data);
        }),
      );
  }

  @Bind()
  notificationOperator<T>(
    messages?: {
      success?: string;
      error?: string;
      info?: string;
    },
    header?: string,
  ): MonoTypeOperatorFunction<T> {
    let error = false;
    let errorResponse: HttpErrorResponse;

    return (source: Observable<T>) =>
      source.pipe(
        catchError((data: HttpErrorResponse) => {
          error = true;
          errorResponse = data;

          return throwError(() => data);
        }),
        finalize(() => {
          if (error) {
            if (messages?.info && errorResponse.error?.translationKey === messages?.info) {
              this.info(messages?.info, header);
              return;
            }

            this.error(messages?.error || errorResponse.error?.translationKey || GENERIC_ERROR_MESSAGE, header);
            return;
          }

          if (messages?.success) {
            this.success(messages.success, header);
          }
        }),
      );
  }
}
